import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Info, ChevronDown, Check, Pause, X, Link, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './SubscriptionAppButton';
import { TagInput } from './TagInput';
import type { TagData } from './TagInput';
import { useTags } from '../contexts/TagsContext';
import type { PackSize } from './PackSizesPanel';
interface PackSizeTier {
  id: string;
  quantity: number;
  label: string;
  color: string;
  loyaltyTier: string;
  discount: string;
}
interface TagItem {
  id: string;
  label: string;
}
interface TaggingEvent {
  id: string;
  title: string;
  description: string;
  icon: 'check' | 'pause' | 'cancel';
  tags: TagData[];
}
interface MembershipPanelProps {
  onNext?: () => void;
  onBack?: () => void;
  packSizes?: PackSize[];
}
export function MembershipPanel({
  onNext,
  onBack,
  packSizes = []
}: MembershipPanelProps) {
  const {
    allTags,
    createTag
  } = useTags();
  // Tier Mapping State
  const [packSizeTiers, setPackSizeTiers] = useState<PackSizeTier[]>([{
    id: '1',
    quantity: 3,
    label: 'Taster Pack',
    color: '#F59E0B',
    loyaltyTier: 'Bronze',
    discount: '5'
  }, {
    id: '2',
    quantity: 6,
    label: 'Half Dozen',
    color: '#0EA5E9',
    loyaltyTier: 'Silver',
    discount: '10'
  }, {
    id: '3',
    quantity: 12,
    label: 'Full Dozen',
    color: '#8B5CF6',
    loyaltyTier: 'Gold',
    discount: '15'
  }]);
  // Subscriber Discount Settings State
  const [eligibleProducts, setEligibleProducts] = useState('wine-only');
  const [discountStacking, setDiscountStacking] = useState('best-wins');
  const [checkoutMessage, setCheckoutMessage] = useState('Wine Club Member ({tier} — {percent}% off)');
  const [pausedBehaviour, setPausedBehaviour] = useState('reduced');
  // Auto-Tagging State
  const [taggingEvents, setTaggingEvents] = useState<TaggingEvent[]>([{
    id: 'subscribe',
    title: 'On Subscribe',
    description: 'Applied when a customer joins this box',
    icon: 'check',
    tags: [{
      id: 'subscription-active',
      label: 'subscription-active',
      color: 'blue'
    }, {
      id: 'tier-dynamic',
      label: 'tier-{dynamic}',
      color: 'blue'
    }, {
      id: 'winepik-subscriber',
      label: 'winepik_subscriber',
      color: 'green'
    }, {
      id: 'estate-club-member',
      label: 'estate-club-member',
      color: 'purple'
    }, {
      id: 'new-member',
      label: 'new-member',
      color: 'green'
    }]
  }, {
    id: 'pause',
    title: 'On Pause',
    description: 'Applied when a member pauses — subscription-active is removed automatically',
    icon: 'pause',
    tags: [{
      id: 'subscription-paused',
      label: 'subscription-paused',
      color: 'orange'
    }]
  }, {
    id: 'cancel',
    title: 'On Cancel',
    description: 'Applied when a member cancels — subscription-active and tier tags are removed automatically',
    icon: 'cancel',
    tags: [{
      id: 'subscription-cancelled',
      label: 'subscription-cancelled',
      color: 'gray'
    }, {
      id: 'winback-eligible',
      label: 'winback-eligible',
      color: 'orange'
    }]
  }]);
  // Tier badge rendering helper
  const TIER_COLORS: Record<string, string> = {
    Bronze: '#CD7F32',
    Silver: '#A0AEC0',
    Gold: '#D69E2E',
    Platinum: '#7C3AED'
  };
  const PACK_ICON_COLORS = ['#0EA5E9', '#8B5CF6', '#F59E0B', '#10B981'];
  const renderTierBadge = (tier: string | undefined) => {
    if (!tier || tier === 'None' || tier === '') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F1F5F9] text-[#94A3B8]" data-id="element-3422" data-mp="g4aqy404ox0d">
          None
        </span>;
    }
    const color = TIER_COLORS[tier];
    if (color) {
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F8FAFC] border border-[#E2E8F0] text-[#0F172A]" data-id="element-3423" data-mp="uzqlsb8j8wxt">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{
          backgroundColor: color
        }} data-id="element-3424" data-mp="zs9zd877zs2m" />
          {tier}
        </span>;
    }
    // Custom tier
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F1F5F9] border border-[#E2E8F0] text-[#475569]" data-id="element-3425" data-mp="32942e02awc0g">
        {tier}
      </span>;
  };
  const handleTierChange = (packId: string, field: 'loyaltyTier' | 'discount', value: string) => {
    setPackSizeTiers(prev => prev.map(pack => pack.id === packId ? {
      ...pack,
      [field]: value
    } : pack));
  };
  const handleRemoveTag = (eventId: string, tagId: string) => {
    setTaggingEvents(prev => prev.map(event => event.id === eventId ? {
      ...event,
      tags: event.tags.filter(tag => tag.id !== tagId)
    } : event));
  };
  const handleAddTag = (eventId: string, tag: TagData) => {
    setTaggingEvents(prev => prev.map(event => event.id === eventId ? {
      ...event,
      tags: event.tags.some(t => t.id === tag.id) ? event.tags : [...event.tags, tag]
    } : event));
  };
  const handleCreateTag = (eventId: string, label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as TagData['color']
    };
    // Register in global tag system
    createTag(newTag);
    // Add to this event
    handleAddTag(eventId, newTag);
  };
  const getEventIcon = (icon: 'check' | 'pause' | 'cancel') => {
    switch (icon) {
      case 'check':
        return <div className="w-8 h-8 rounded-lg bg-[#D1FAE5] flex items-center justify-center" data-id="element-3426" data-mp="140gcg8fnhdb0">
            <Check className="w-4 h-4 text-[#059669]" data-id="element-3427" data-mp="18rewnj3revjj" />
          </div>;
      case 'pause':
        return <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center" data-id="element-3428" data-mp="1drjdja8kxsaa">
            <Pause className="w-4 h-4 text-[#D97706]" data-id="element-3429" data-mp="1hswh1do79je1" />
          </div>;
      case 'cancel':
        return <div className="w-8 h-8 rounded-lg bg-[#FEE2E2] flex items-center justify-center" data-id="element-3430" data-mp="1mrg5plu6h9a3">
            <X className="w-4 h-4 text-[#DC2626]" data-id="element-3431" data-mp="3iukx51hafpk4" />
          </div>;
    }
  };
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-3432" data-mp="3ishy5ry5qm39">
      {/* Section 1: Tier Mapping */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3433" data-mp="3erfvt28y1tx2">
        <div className="p-6" data-id="element-3434" data-mp="3slfkk969sv75">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3435" data-mp="3smqktz0ibkti">
            Tier Mapping
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3436" data-mp="6dfoa9cp7er6">
            Tier assignments are configured per frequency in the Pack Sizes tab.
            This is a live summary.
          </p>

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 mb-4" data-id="element-3437" data-mp="fenjujnty1hk">
            <div className="col-span-5" data-id="element-3438" data-mp="fc1dgncods0x">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3439" data-mp="aej631eb3kv1">
                PACK / FREQUENCY
              </span>
            </div>
            <div className="col-span-4" data-id="element-3440" data-mp="2c6fdbytfy8k0">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3441" data-mp="2c7h2t8b7exl9">
                LOYALTY TIER
              </span>
            </div>
            <div className="col-span-3" data-id="element-3442" data-mp="wn6og1mh7yyy">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3443" data-mp="wm51ays6rm0v">
                AD-HOC DISCOUNT
              </span>
            </div>
          </div>

          {/* Derived rows from packSizes */}
          {!packSizes || packSizes.length === 0 ? <div className="py-10 text-center text-[#94A3B8] bg-[#F8FAFC] rounded-lg border border-dashed border-[#E2E8F0]" data-id="element-3444" data-mp="8xc99deopybj">
              <p className="text-[14px]" data-id="element-3445" data-mp="dxrmm4yyv5ti">
                No pack sizes configured yet. Add pack sizes in the Pack Sizes
                tab to see tier assignments here.
              </p>
            </div> : <div className="space-y-3" data-id="element-3446" data-mp="3zk8dtw2b1v7">
              {packSizes.map((pack, packIndex) => pack.frequencies.map(freq => <div key={`${pack.id}-${freq.id}`} className="grid grid-cols-12 gap-4 items-center py-2 border-b border-[#F1F5F9] last:border-0" data-id="element-3447" data-mp="1ubq6pchi9fik">
                    {/* Pack icon + name + frequency */}
                    <div className="col-span-5 flex items-center gap-3" data-id="element-3448" data-mp="m2x6zwzzqf2i">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0" style={{
                backgroundColor: PACK_ICON_COLORS[packIndex % PACK_ICON_COLORS.length]
              }} data-id="element-3449" data-mp="h5eslr37f7n4">
                        {pack.quantity || pack.number}
                      </div>
                      <div className="min-w-0" data-id="element-3450" data-mp="3e9yf4ilv7k6">
                        <div className="text-[14px] font-medium text-[#0F172A] truncate" data-id="element-3451" data-mp="3uiqityjdn2d8">
                          {pack.displayLabel || pack.title}
                        </div>
                        <div className="text-[12px] text-[#94A3B8] truncate" data-id="element-3452" data-mp="3kl3vcno0i2hn">
                          {freq.interval || freq.name || 'No interval set'}
                        </div>
                      </div>
                    </div>

                    {/* Loyalty Tier badge (read-only) */}
                    <div className="col-span-4" data-id="element-3453" data-mp="1rapx2wnvnm92">
                      {renderTierBadge(freq.loyaltyTier)}
                    </div>

                    {/* Ad-hoc discount (read-only) */}
                    <div className="col-span-3" data-id="element-3454" data-mp="1v9ys04unlq67">
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[6px] bg-[#F8FAFC] border border-[#E2E8F0] text-[14px] font-medium text-[#0F172A]" data-id="element-3455" data-mp="204322wz7uebi">
                        {freq.adHocDiscount ?? '0'}
                        <span className="text-[#64748B] text-[13px]" data-id="element-3456" data-mp="18doq56arjc2h">%</span>
                      </span>
                    </div>
                  </div>))}
            </div>}

          {/* Info Banner */}
          <div className="mt-6 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-3457" data-mp="1hb8uio3x9ohb">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-3458" data-mp="1mcy8ocmfyi9m" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-3459" data-mp="2sqxgpd9cqjh2">
              The{' '}
              <span className="font-semibold text-[#0F172A]" data-id="element-3460" data-mp="2xpqy3grreumi">
                ad-hoc discount
              </span>{' '}
              applies automatically at checkout when a member purchases products
              outside their subscription box. Powered by a Shopify Discount
              Function — no codes required.
            </p>
          </div>
        </div>
      </Card>

      {/* Section 2: Subscriber Discount Settings */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3461" data-mp="gb89gcx65pwt">
        <div className="p-6" data-id="element-3462" data-mp="3fs6fh3hfb54r">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3463" data-mp="3fszdng2bgmba">
            Subscriber Discount Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3464" data-mp="3ort8b0w372m7">
            Configure how the ad-hoc member discount behaves at checkout
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3465" data-mp="3trpacul84jz4">
            {/* Eligible Products */}
            <div data-id="element-3466" data-mp="1412hmh6jmqaq">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3467" data-mp="143ooeelm11rw">
                Eligible Products
              </label>
              <div className="relative" data-id="element-3468" data-mp="1j559xhs11jma">
                <select value={eligibleProducts} onChange={e => setEligibleProducts(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3469" data-mp="1j4401gmqz365">
                  <option value="wine-only" data-id="element-3470" data-mp="15beaj280fg4a">Wine products only</option>
                  <option value="all" data-id="element-3471" data-mp="10fp88r4lvl4a">All products</option>
                  <option value="tagged" data-id="element-3472" data-mp="vei0ut6dkny5">Tagged products only</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3473" data-mp="qfonr1v02yyi" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-3474" data-mp="mfxn86fo94gs">
                Which products the subscriber discount applies to. Merchandise
                and accessories can be excluded.
              </p>
            </div>

            {/* Discount Stacking */}
            <div data-id="element-3475" data-mp="e5grfq7033x8">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3476" data-mp="e4x9wal9ihjq">
                Discount Stacking
              </label>
              <div className="relative" data-id="element-3477" data-mp="iy9zoo1ogue2">
                <select value={discountStacking} onChange={e => setDiscountStacking(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3478" data-mp="55tsdb1qua7c">
                  <option value="best-wins" data-id="element-3479" data-mp="11urmqs5l063z">Best discount wins</option>
                  <option value="stack" data-id="element-3480" data-mp="nwbeuxvzg9k9">Stack discounts</option>
                  <option value="member-only" data-id="element-3481" data-mp="sulb7btg1g7g">Member discount only</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3482" data-mp="38lr25ivztg55" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-3483" data-mp="3dlvmxl3xox16">
                How the member discount interacts with sale prices, discount
                codes, and other automatic discounts.
              </p>
            </div>

            {/* Checkout Message */}
            <div data-id="element-3484" data-mp="3f1rbavz60nn8">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3485" data-mp="3f29aa0d7pfyu">
                Checkout Message
              </label>
              <input type="text" value={checkoutMessage} onChange={e => setCheckoutMessage(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3486" data-mp="3a0trkeyibljm" />

              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-3487" data-mp="2rceyzn8jjzgp">
                Displayed at checkout. Use {'{tier}'} and {'{percent}'} as
                dynamic placeholders.
              </p>
            </div>

            {/* Paused Subscriber Behaviour */}
            <div data-id="element-3488" data-mp="1ynhj1qrsyer4">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3489" data-mp="1ylx0kzirim9m">
                Paused Subscriber Behaviour
              </label>
              <div className="relative" data-id="element-3490" data-mp="16wikk348sg63">
                <select value={pausedBehaviour} onChange={e => setPausedBehaviour(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3491" data-mp="16tcy6vmzzse2">
                  <option value="reduced" data-id="element-3492" data-mp="1plow966mc7ie">Reduced discount while paused</option>
                  <option value="none" data-id="element-3493" data-mp="1pnrwsk5edj1d">No discount while paused</option>
                  <option value="full" data-id="element-3494" data-mp="1uljll5aqwp1c">Full discount while paused</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3495" data-mp="1ym3jwc721yp2" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-3496" data-mp="48cdrmnxpwyd">
                What happens to the ad-hoc discount when a member pauses their
                subscription.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 3: Auto-Tagging */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3497" data-mp="3o1xu65acjw04">
        <div className="p-6" data-id="element-3498" data-mp="3j57al7zc2i9y">
          <div className="flex items-start justify-between mb-1" data-id="element-3499" data-mp="3j2bc9ijkprkz">
            <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-3500" data-mp="3f4ni4hppg2u5">
              Auto-Tagging
            </h2>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#64748B] bg-white border border-[#E2E8F0] rounded-md hover:bg-[#F8FAFC] transition-colors" data-id="element-3501" data-mp="fluvgp17phnx">
              <Link className="w-3.5 h-3.5" data-id="element-3502" data-mp="6pdidg9af9pj" />
              Extends global tagging rules
            </button>
          </div>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3503" data-mp="2rdi2a2jrlaju">
            Tags applied to Shopify customers based on subscription lifecycle
            events. These sync to Klaviyo for segmentation.
          </p>

          <div className="space-y-6" data-id="element-3504" data-mp="2nvu1higrf3z6">
            {taggingEvents.map((event, index) => <div key={event.id} data-id="element-3505" data-mp="2ntrdf3z63kx3" data-mpi={index}>
                {index > 0 && <div className="h-px bg-[#E2E8F0] mb-6" data-id="element-3506" data-mp="2stm3x6f168hm" />}

                <div className="flex items-start gap-3 mb-4" data-id="element-3507" data-mp="2euw4vl8nhyqm">
                  {getEventIcon(event.icon)}
                  <div data-id="element-3508" data-mp="2iw7o990xlnqd">
                    <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3509" data-mp="2ivg1mnpbb0c8">
                      {event.title}
                    </h3>
                    <p className="text-[13px] text-[#94A3B8]" data-id="element-3510" data-mp="252zupbtaii61">
                      {event.description}
                    </p>
                  </div>
                </div>

                {/* Tags - using TagInput component */}
                <TagInput tags={event.tags} availableTags={allTags} onAddTag={tag => handleAddTag(event.id, tag)} onRemoveTag={tagId => handleRemoveTag(event.id, tagId)} onCreateTag={(label, color) => handleCreateTag(event.id, label, color)} placeholder="Search or create a tag..." data-id="element-3511" data-mp="205q803xacklw" />
              </div>)}
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between pt-2" data-id="element-3512" data-mp="17kb2ty25ezfj">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-3513" data-mp="12kyf7b3gdobw">
          <ArrowLeft className="w-4 h-4" data-id="element-3514" data-mp="1chte3v3n1qqp" />
          Pack Sizes
        </button>
        <Button variant="primary" onClick={onNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-3516" data-mp="1qgavlb2ya706" />} data-id="element-3515" data-mp="1qhm1bgfyhotx">
          Next: Flow
        </Button>
      </div>
    </div>;
}